import React, { useCallback, useEffect, useState } from "react";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage } from "../../../customComponents/CustomAlertMessage";
import { DISABLE_ALL_NETWORK } from "../../../networkConfig";
import "./activityLogs.css";

function extractLogsArray(res) {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (d?.data && Array.isArray(d.data)) return d.data;
  return [];
}

function formatActivityAt(iso) {
  if (iso == null || iso === "") return "";
  const d = typeof iso === "string" ? new Date(iso) : iso instanceof Date ? iso : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function buildDescription(entry) {
  const m = entry.metadata && typeof entry.metadata === "object" ? entry.metadata : {};
  const dv = entry.device && typeof entry.device === "object" ? entry.device : {};
  const parts = [];
  if (entry.category) parts.push(`[${entry.category}]`);
  if (entry.Activity != null || entry.activity != null) parts.push(String(entry.Activity ?? entry.activity));
  if (m.method) parts.push(`via ${m.method}`);
  if (dv.browser) parts.push(`${dv.browser}${dv.browserVersion ? " " + dv.browserVersion : ""}`);
  if (m.email != null && m.email !== "") parts.push(`${m.email}`);
  if (entry.status != null && entry.status !== "") parts.push(String(entry.status));
  const joined = parts.join(" · ").trim();
  return joined || "—";
}

function apiEntryToRow(item) {
  const ipRaw = item.IP ?? item.ip ?? "";
  const whenRaw = item.createdAt ?? item.date ?? "";
  return {
    id: item._id ?? item.id ?? `${whenRaw}-${Math.random()}`,
    activity: String(item.Activity ?? item.activity ?? ""),
    description: buildDescription(item),
    ip: String(ipRaw),
    at: formatActivityAt(whenRaw),
  };
}

const ActivitylogPage = () => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (DISABLE_ALL_NETWORK) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await AuthService.getActivityLogs();
        if (cancelled) return;
        if (res?.success === false && res?.message) alertErrorMessage(res.message);
        const list = extractLogsArray(res);
        if (cancelled) return;
        const mapped = list.map(apiEntryToRow);
        if (mapped.length) setRows(mapped);
        else setRows([]);
      } catch {
        if (!cancelled) alertErrorMessage("Could not load activity logs.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleExport = useCallback(() => {
    const header = ["Activity", "Description", "IP Address", "Date & Time"];
    const lines = rows.map((r) => `"${String(r.activity).replace(/"/g, '""')}","${String(r.description).replace(/"/g, '""')}","${String(r.ip)}","${String(r.at)}"`);
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "activity-logs.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section Overview_mid">
        <div className="kyc_approval_s activity_logs agce-activity-logs-wrap">
          <div className="cnt agce-activity-logs">
            <div className="agce-activity-logs__head">
              <div>
                <h1 className="agce-activity-logs__title">Activity Logs</h1>
                <p className="agce-activity-logs__sub">
                  Track and manage all your support requests in one place.
                </p>
              </div>
              <button
                type="button"
                className="agce-activity-logs__export"
                onClick={handleExport}
              >
                <i className="ri-download-2-line" aria-hidden="true" />
                Export Logs
              </button>
            </div>

            <div className="agce-activity-logs__tablewrap agce-activity-logs__tablewrap--desktop">
              <table className="agce-activity-logs__table">
                <thead>
                  <tr>
                    <th scope="col">Activity</th>
                    <th scope="col">Description</th>
                    <th scope="col">IP Address</th>
                    <th scope="col">Date &amp; Time</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className="agce-activity-logs__activity-cell">
                          <span className="agce-activity-logs__act-badge" aria-hidden="true">
                           <img src="/images/useractive-logs.svg" alt="user" />
                          </span>
                          <strong>{row.activity}</strong>
                        </div>
                      </td>
                      <td className="agce-activity-logs__col-desc">{row.description}</td>
                      <td>{row.ip}</td>
                      <td className="agce-activity-logs__col-time">{row.at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="agce-activity-logs__cards" aria-label="Activity log entries">
              {rows.map((row) => (
                <li key={row.id} className="agce-activity-logs__card">
                  <div className="agce-activity-logs__card-head">
                    <span className="agce-activity-logs__act-badge" aria-hidden="true">
                      <img src="/images/useractive-logs.svg" alt="user" />
                    </span>
                    <strong>{row.activity}</strong>
                  </div>
                  <div className="agce-activity-logs__card-row">
                    <span className="agce-activity-logs__card-label">Description</span>
                    <span className="agce-activity-logs__card-val">{row.description}</span>
                  </div>
                  <div className="agce-activity-logs__card-row">
                    <span className="agce-activity-logs__card-label">IP Address</span>
                    <span className="agce-activity-logs__card-val">{row.ip}</span>
                  </div>
                  <div className="agce-activity-logs__card-row">
                    <span className="agce-activity-logs__card-label">Date &amp; Time</span>
                    <span className="agce-activity-logs__card-val agce-activity-logs__card-val--time">
                      {row.at}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivitylogPage;
